import { useState, useEffect, useRef } from "react";
import Logo from "@/components/base/Logo";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import SearchModal from "@/components/feature/SearchModal";

const aboutDropdown = [
  { name: "About Us", href: "/about" },
  { name: "Director's Message", href: "/about/director-message" },
  { name: "Story", href: "/about/story" },
  { name: "Aim", href: "/about/aim" },
  { name: "Campus", href: "/about/campus" },
  { name: "Organisation", href: "/about/organisation" },
];

const navLinks = [
  { name: "Courses", href: "/courses" },
  { name: "About", href: "/about", hasDropdown: true },
  { name: "News & Media", href: "/news-events" },
  { name: "FAQ", href: "/faq" },
  { name: "Downloads", href: "/downloads" },
  { name: "Past Expeditions", href: "/past-expeditions" },
  { name: "Contact", href: "/contact" },
];

export default function Navbar() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [aboutDropdownOpen, setAboutDropdownOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const aboutDropdownRef = useRef<HTMLDivElement>(null);
  // The mobile About submenu is a separate DOM tree from the desktop one
  // (only rendered when the hamburger menu is open) — it needs its own ref
  // so the outside-click handler below doesn't treat a tap on a mobile
  // submenu link as "outside" the (desktop-only) aboutDropdownRef and close
  // the menu before the tap's click/navigation event can fire.
  const mobileAboutDropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin, loading: authLoading } = useAuth();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
    setAboutDropdownOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
      const target = e.target as Node;
      const insideDesktopAbout = aboutDropdownRef.current?.contains(target);
      const insideMobileAbout = mobileAboutDropdownRef.current?.contains(target);
      if (!insideDesktopAbout && !insideMobileAbout) {
        setAboutDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleLogout = async () => {
    await logout();
    setDropdownOpen(false);
    navigate("/");
  };

  const userInitials = user?.user_metadata?.full_name
    ? (user.user_metadata.full_name as string).split(" ").map((n: string) => n[0]).join("").toUpperCase().slice(0, 2)
    : user?.email?.slice(0, 2).toUpperCase() ?? "?";

  const isHome = location.pathname === "/";
  const isAboutPage = location.pathname.startsWith("/about");

  return (
    <>
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isHome && !scrolled
            ? "bg-transparent"
            : "bg-white/95 backdrop-blur-md border-b border-gray-100 shadow-sm"
        }`}
        role="navigation"
        aria-label="Main navigation"
      >
        <div className="max-w-7xl mx-auto px-4 lg:px-8">
          <div className="flex items-center justify-between h-[72px] md:h-20">
            {/* Logo */}
            <Link
              to="/"
              className="flex items-center space-x-3 cursor-pointer flex-shrink-0 group"
            >
              <Logo width={64} height={64} className="h-14 w-14 md:h-16 md:w-16 transition-transform duration-300 group-hover:scale-110" />
              <div className="hidden sm:block">
                <span
                  className={`font-heading font-bold text-sm md:text-base tracking-wide whitespace-nowrap transition-colors duration-300 ${
                    isHome && !scrolled ? "text-secondary" : "text-secondary"
                  }`}
                >
                  NIMAS Dirang
                </span>
                <span className="block text-[10px] text-gray-400 -mt-0.5 font-medium tracking-wider uppercase">
                  Ministry of Defence, India
                </span>
              </div>
            </Link>

            {/* Desktop Nav Links */}
            <div className="hidden lg:flex items-center space-x-0.5">
              {navLinks.map((link) => {
                const isActive = location.pathname === link.href ||
                  (link.href !== "/" && location.pathname.startsWith(link.href));
                if (link.hasDropdown) {
                  return (
                    <div key={link.name} className="relative" ref={aboutDropdownRef}>
                      <button
                        onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
                        className={`relative px-3.5 py-2 text-sm font-medium transition-all duration-200 rounded-lg whitespace-nowrap cursor-pointer flex items-center gap-1 ${
                          isAboutPage
                            ? "text-primary"
                            : isHome && !scrolled
                            ? "text-gray-600 hover:text-primary hover:bg-white/60"
                            : "text-gray-600 hover:text-primary hover:bg-gray-50"
                        }`}
                      >
                        {link.name}
                        <i className={`ri-arrow-down-s-line text-sm transition-transform duration-200 ${aboutDropdownOpen ? "rotate-180" : ""}`} />
                        {isAboutPage && (
                          <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-primary rounded-full" />
                        )}
                      </button>
                      {aboutDropdownOpen && (
                        <div className="absolute top-full left-0 mt-2 w-52 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 overflow-hidden">
                          {aboutDropdown.map((item) => (
                            <Link
                              key={item.name}
                              to={item.href}
                              className={`flex items-center gap-3 px-4 py-2.5 text-sm transition-colors cursor-pointer ${
                                location.pathname === item.href
                                  ? "text-primary bg-primary/5 font-medium"
                                  : "text-gray-700 hover:bg-gray-50 hover:text-primary"
                              }`}
                            >
                              {item.name}
                            </Link>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                }
                return (
                  <Link
                    key={link.name}
                    to={link.href}
                    className={`relative px-3.5 py-2 text-sm font-medium transition-all duration-200 rounded-lg whitespace-nowrap cursor-pointer ${
                      isActive
                        ? "text-primary"
                        : isHome && !scrolled
                        ? "text-gray-600 hover:text-primary hover:bg-white/60"
                        : "text-gray-600 hover:text-primary hover:bg-gray-50"
                    }`}
                  >
                    {link.name}
                    {isActive && (
                      <span className="absolute bottom-0 left-1/2 -translate-x-1/2 w-4 h-0.5 bg-primary rounded-full" />
                    )}
                  </Link>
                );
              })}
            </div>

            {/* Right Section */}
            <div className="flex items-center gap-2 md:gap-3">
              {/* Search */}
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2.5 rounded-xl text-gray-500 hover:text-foreground-950 hover:bg-gray-100 transition-all duration-200 cursor-pointer"
                aria-label="Search"
              >
                <i className="ri-search-line text-lg" />
              </button>
              {authLoading ? (
                <div className="w-8 h-8 rounded-full bg-gray-100 animate-pulse" />
              ) : user ? (
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center space-x-2 pl-1 pr-2 py-1 rounded-full hover:bg-gray-50 transition-all duration-200 cursor-pointer border border-gray-100"
                  >
                    <div className="w-8 h-8 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-xs font-bold">{userInitials}</span>
                    </div>
                    <span className="hidden md:block text-sm font-medium text-gray-700 max-w-[100px] truncate">
                      {user.user_metadata?.full_name ?? user.email?.split("@")[0]}
                    </span>
                    <i className={`ri-arrow-down-s-line text-sm text-gray-400 transition-transform duration-200 ${dropdownOpen ? "rotate-180" : ""}`} />
                    {isAdmin && (
                      <span className="hidden md:inline-flex text-[10px] font-bold bg-amber-100 text-amber-700 px-1.5 py-0.5 rounded-full uppercase tracking-wider">
                        Admin
                      </span>
                    )}
                  </button>

                  {dropdownOpen && (
                    <div className="absolute right-0 top-full mt-2 w-60 bg-white rounded-xl shadow-xl border border-gray-100 py-2 z-50 overflow-hidden">
                      <div className="px-4 py-3 border-b border-gray-50">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="text-white text-sm font-bold">{userInitials}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="text-sm font-semibold text-secondary truncate">
                              {user.user_metadata?.full_name ?? "User"}
                            </p>
                            <p className="text-xs text-gray-400 truncate">{user.email}</p>
                            {isAdmin && (
                              <span className="inline-block mt-1 text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase tracking-wider">
                                Administrator
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {isAdmin ? (
                        <Link
                          to="/admin/dashboard"
                          className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                        >
                          <div className="w-6 h-6 flex items-center justify-center rounded-md bg-amber-50">
                            <i className="ri-shield-line text-amber-600 text-sm" />
                          </div>
                          Admin Panel
                        </Link>
                      ) : (
                        <>
                          <Link
                            to="/student/dashboard"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <div className="w-6 h-6 flex items-center justify-center rounded-md bg-blue-50">
                              <i className="ri-dashboard-line text-blue-500 text-sm" />
                            </div>
                            Dashboard
                          </Link>
                          <Link
                            to="/student/bookings"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <div className="w-6 h-6 flex items-center justify-center rounded-md bg-emerald-50">
                              <i className="ri-calendar-check-line text-emerald-500 text-sm" />
                            </div>
                            My Bookings
                          </Link>
                          <Link
                            to="/student/payments"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <div className="w-6 h-6 flex items-center justify-center rounded-md bg-purple-50">
                              <i className="ri-bank-card-line text-purple-500 text-sm" />
                            </div>
                            My Payments
                          </Link>
                          <div className="border-t border-gray-50 my-1" />
                          <Link
                            to="/student/profile"
                            className="flex items-center gap-3 px-4 py-2.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors cursor-pointer"
                          >
                            <div className="w-6 h-6 flex items-center justify-center rounded-md bg-gray-50">
                              <i className="ri-user-settings-line text-gray-500 text-sm" />
                            </div>
                            Profile Settings
                          </Link>
                        </>
                      )}

                      <div className="border-t border-gray-50 mt-1 pt-1">
                        <button
                          onClick={handleLogout}
                          className="flex items-center gap-3 w-full px-4 py-2.5 text-sm text-red-600 hover:bg-red-50 transition-colors cursor-pointer"
                        >
                          <div className="w-6 h-6 flex items-center justify-center rounded-md bg-red-50">
                            <i className="ri-logout-box-r-line text-red-500 text-sm" />
                          </div>
                          Sign Out
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <Link
                    to="/student/login"
                    className="hidden sm:inline-flex px-5 py-2 text-sm font-semibold text-secondary hover:text-primary border border-gray-200 hover:border-primary/30 bg-white hover:bg-primary/5 rounded-xl transition-all duration-200 whitespace-nowrap cursor-pointer"
                  >
                    Student Login
                  </Link>
                  {/* Mobile gets "Student Login" in place of "Sign Up" —
                      same prominent CTA styling, just a different
                      destination/label below the sm breakpoint. */}
                  <Link
                    to="/student/login"
                    className="sm:hidden px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary-dark text-white rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 whitespace-nowrap"
                  >
                    Student Login
                  </Link>
                  <Link
                    to="/auth/signup"
                    className="hidden sm:inline-flex px-4 md:px-5 py-2 md:py-2.5 text-sm font-semibold bg-primary hover:bg-primary-dark text-white rounded-xl transition-all duration-300 hover:shadow-lg hover:shadow-primary/20 whitespace-nowrap"
                  >
                    Sign Up
                  </Link>
                </div>
              )}

              {/* Mobile hamburger */}
              <button
                className="lg:hidden p-2 cursor-pointer rounded-lg hover:bg-gray-50 transition-colors text-gray-700"
                aria-label="Toggle menu"
                onClick={() => setMobileOpen(!mobileOpen)}
              >
                <i
                  className={`text-xl ${mobileOpen ? "ri-close-line" : "ri-menu-line"}`}
                  aria-hidden="true"
                />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileOpen && (
          <div className="lg:hidden bg-white border-t border-gray-100 px-4 py-5 space-y-1 shadow-xl max-h-[80vh] overflow-y-auto">
            {/* Mobile About Dropdown */}
            <div className="space-y-1" ref={mobileAboutDropdownRef}>
              <button
                onClick={() => setAboutDropdownOpen(!aboutDropdownOpen)}
                className={`flex items-center justify-between w-full px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                  isAboutPage
                    ? "text-primary bg-primary/5"
                    : "text-gray-700 hover:text-primary hover:bg-gray-50"
                }`}
              >
                <span className="flex items-center gap-3">
                  About
                </span>
                <i className={`ri-arrow-down-s-line text-lg transition-transform duration-200 ${aboutDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {aboutDropdownOpen && (
                <div className="ml-4 space-y-1 border-l-2 border-gray-100 pl-4">
                  {aboutDropdown.map((item) => (
                    <Link
                      key={item.name}
                      to={item.href}
                      className={`flex items-center gap-3 px-4 py-2.5 text-sm font-medium rounded-xl transition-colors ${
                        location.pathname === item.href
                          ? "text-primary bg-primary/5"
                          : "text-gray-600 hover:text-primary hover:bg-gray-50"
                      }`}
                    >
                      {item.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {navLinks.filter(l => !l.hasDropdown).map((link) => {
              const isActive = location.pathname === link.href;
              return (
                <Link
                  key={link.name}
                  to={link.href}
                  className={`flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl transition-colors ${
                    isActive
                      ? "text-primary bg-primary/5"
                      : "text-gray-700 hover:text-primary hover:bg-gray-50"
                  }`}
                >
                  {link.name}
                  {isActive && <i className="ri-arrow-right-line ml-auto text-primary" />}
                </Link>
              );
            })}

            <div className="border-t border-gray-100 my-3" />

            {user ? (
              <>
                <div className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-secondary rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-white text-sm font-bold">{userInitials}</span>
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-secondary truncate">
                        {user.user_metadata?.full_name ?? "User"}
                      </p>
                      <p className="text-xs text-gray-400 truncate">{user.email}</p>
                      {isAdmin && (
                        <span className="inline-block mt-1 text-[10px] font-semibold bg-amber-100 text-amber-700 px-2 py-0.5 rounded-full uppercase tracking-wider">
                          Admin
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {isAdmin ? (
                  <Link
                    to="/admin/dashboard"
                    className="flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl text-gray-700 hover:text-primary hover:bg-gray-50"
                  >
                    <i className="ri-shield-line text-base text-amber-500" />
                    Admin Panel
                  </Link>
                ) : (
                  <>
                    <Link
                      to="/student/dashboard"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl text-gray-700 hover:text-primary hover:bg-gray-50"
                    >
                      <i className="ri-dashboard-line text-base text-blue-500" />
                      Dashboard
                    </Link>
                    <Link
                      to="/student/bookings"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl text-gray-700 hover:text-primary hover:bg-gray-50"
                    >
                      <i className="ri-calendar-check-line text-base text-emerald-500" />
                      My Bookings
                    </Link>
                    <Link
                      to="/student/payments"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl text-gray-700 hover:text-primary hover:bg-gray-50"
                    >
                      <i className="ri-bank-card-line text-base text-purple-500" />
                      My Payments
                    </Link>
                    <Link
                      to="/student/profile"
                      className="flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl text-gray-700 hover:text-primary hover:bg-gray-50"
                    >
                      <i className="ri-user-settings-line text-base text-gray-500" />
                      Profile
                    </Link>
                  </>
                )}

                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 w-full px-4 py-3 text-sm font-medium rounded-xl text-red-600 hover:bg-red-50 cursor-pointer"
                >
                  <i className="ri-logout-box-r-line text-base" />
                  Sign Out
                </button>
              </>
            ) : (
              <>
                <Link
                  to="/student/login"
                  className="flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl text-gray-700 hover:text-primary hover:bg-gray-50"
                >
                  <i className="ri-login-circle-line text-base text-gray-400" />
                  Student Login
                </Link>
                <Link
                  to="/auth/signup"
                  className="flex items-center gap-3 px-4 py-3 text-sm font-medium rounded-xl text-primary hover:bg-primary/5"
                >
                  <i className="ri-user-add-line text-base" />
                  Sign Up
                </Link>
              </>
            )}


          </div>
        )}
      </nav>
      <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
    </>
  );
}